import type { TranslationService } from '../services/i18n/translation.service';
import { REEL_WORDS } from './presentation';
import { writtenText } from './written-text';

/**
 * An order line's unit as written, or '' when it has none: blank, or "null" as a 2026-03 load saved it. A line with
 * no unit is shown and saved with none, for a person to pick, never as tons or MT (card 943).
 */
export const writtenUnit = writtenText;

/** An item's unit as written: its quantity_unit, else its unit (an AI's product, an order or product line) */
export function itemUnit(
  item: { quantity_unit?: string | null; unit?: string | null } | null | undefined
): string {
  return writtenUnit(item?.quantity_unit) || writtenUnit(item?.unit);
}

/**
 * Display label for an order quantity unit.
 * "tons" and "MT" stay distinct. An empty value uses the fallback.
 */
export function quantityUnitLabel(unit: string | null | undefined, fallback = ''): string {
  const raw = writtenUnit(unit);
  if (!raw) return fallback;
  const key = raw.toLowerCase();
  if (key === 'mt' || key === 'metric ton' || key === 'metric tons') return 'MT';
  if (key === 'ton' || key === 'tons' || key === 't') return 'tons';
  if (key === 'kg' || key === 'kilogram' || key === 'kilograms') return 'kg';
  return raw;
}

/** What a unit measures: a count of things, a weight, or a size */
export type UnitKind = 'count' | 'weight' | 'size';

interface UnitWords {
  /** The unit as the screens compare it */
  unit: string;
  kind: UnitKind;
  /** The package one of a counted unit is, as the packing list prints it (server unit_label) */
  package?: string;
  /** Every way an order line writes it, in lower case */
  words: string[];
}

/**
 * The units order lines are written in, in English and Spanish: one table for every screen that reads a unit (the
 * pickers, the totals, the packing list count). Reels and rolls are one unit, as the server counts them
 * (apps/common/units.py _REEL_UNITS).
 */
const UNIT_WORDS: readonly UnitWords[] = [
  { unit: 'MT', kind: 'weight', words: ['mt', 'tm', 'metric ton', 'metric tons'] },
  {
    unit: 'tons',
    kind: 'weight',
    words: ['t', 'ton', 'tons', 'tonne', 'tonnes', 'tonelada', 'toneladas'],
  },
  {
    unit: 'kg',
    kind: 'weight',
    words: ['kg', 'kgs', 'kilo', 'kilos', 'kilogram', 'kilograms', 'kilogramo', 'kilogramos'],
  },
  { unit: 'lb', kind: 'weight', words: ['lb', 'lbs', 'pound', 'pounds', 'libra', 'libras'] },
  {
    unit: 'rolls',
    kind: 'count',
    package: 'roll',
    words: [...REEL_WORDS, 'reels', 'roll', 'rolls', 'rollo', 'rollos'],
  },
  { unit: 'reams', kind: 'count', package: 'ream', words: ['ream', 'reams', 'resma', 'resmas'] },
  {
    unit: 'sheets',
    kind: 'count',
    package: 'sheet',
    words: ['sheet', 'sheets', 'hoja', 'hojas', 'pliego', 'pliegos'],
  },
  {
    unit: 'pallets',
    kind: 'count',
    package: 'pallet',
    words: ['pallet', 'pallets', 'paleta', 'paletas', 'tarima', 'tarimas'],
  },
  { unit: 'boxes', kind: 'count', package: 'box', words: ['box', 'boxes', 'caja', 'cajas'] },
  {
    unit: 'cartons',
    kind: 'count',
    package: 'box',
    words: ['carton', 'cartons', 'cartón', 'cartones'],
  },
  { unit: 'bales', kind: 'count', package: 'bundle', words: ['bale', 'bales', 'fardo', 'fardos'] },
  {
    unit: 'bundles',
    kind: 'count',
    package: 'bundle',
    words: ['bundle', 'bundles', 'atado', 'atados'],
  },
  {
    unit: 'cases',
    kind: 'count',
    package: 'case',
    words: ['case', 'cases', 'estuche', 'estuches'],
  },
  {
    unit: 'crates',
    kind: 'count',
    package: 'crate',
    words: ['crate', 'crates', 'jaula', 'jaulas'],
  },
  {
    unit: 'pieces',
    kind: 'count',
    package: 'piece',
    words: [
      'piece',
      'pieces',
      'pc',
      'pcs',
      'pieza',
      'piezas',
      'unit',
      'units',
      'unidad',
      'unidades',
    ],
  },
  {
    unit: 'packages',
    kind: 'count',
    package: 'package',
    words: ['package', 'packages', 'bulto', 'bultos'],
  },
  {
    unit: 'SQM',
    kind: 'size',
    words: ['sqm', 'sq m', 'm2', 'm²', 'square meter', 'square meters'],
  },
];
const UNIT_OF_WORD = new Map(
  UNIT_WORDS.flatMap((entry) => entry.words.map((word) => [word, entry]))
);

function unitWords(unit: string | null | undefined): UnitWords | undefined {
  return UNIT_OF_WORD.get((unit || '').trim().toLowerCase());
}

/** What an order line's unit measures; undefined for no unit or a word the table lacks */
export function unitKind(unit: string | null | undefined): UnitKind | undefined {
  return unitWords(unit)?.kind;
}

/** The package one of a counted unit is ("bobinas" are rolls, "piezas" pieces), or undefined */
export function packageOfUnit(unit: string | null | undefined): string | undefined {
  return unitWords(unit)?.package;
}

/** The unit as the screens compare it: one name for all its words, else as quantityUnitLabel writes it */
function canonicalUnit(unit: string | null | undefined): string {
  return unitWords(unit)?.unit ?? quantityUnitLabel(unit);
}

/** One price unit however it is written, for the price per unit: like sameQuantityUnit, and tons are metric tons
 *  as the server counts them (the margin screen and its sidebar share this rule) */
export function samePriceUnit(a: string | null | undefined, b: string | null | undefined): boolean {
  const key = (unit: string | null | undefined) =>
    sameQuantityUnit(unit, 'tons') ? 'mt' : canonicalUnit(unit).toLowerCase();
  return key(a) === key(b);
}

/**
 * The one unit all the given lines are in, as the first line writes it; '' when one has none or they differ (card
 * 989). Tons and MT agree, as the server's order_unit says (apps/common/units.py); kilograms and tons do not. A sum
 * of quantities, or a price per unit times that sum, is only real with this unit.
 */
export function linesUnit(units: readonly (string | null | undefined)[]): string {
  const written = units.map((unit) => writtenUnit(unit));
  if (!written.length || written.some((unit) => !unit)) return '';
  return written.every((unit) => samePriceUnit(unit, written[0])) ? written[0] : '';
}

/** Metric tons in one of a weight unit (prices are per MT: 1,000 kg = 1 MT exactly), as the server's _KG_PER_UNIT */
const TONS_PER_UNIT: Record<string, number> = { MT: 1, tons: 1, kg: 0.001, lb: 0.000454 };

/** A quantity in metric tons, or null when its unit is no weight (rolls, reams, sheets) or missing */
export function quantityInTons(
  quantity: number | string | null | undefined,
  unit: string | null | undefined
): number | null {
  const perUnit = TONS_PER_UNIT[unitWords(unit)?.unit ?? ''];
  const value = parseFloat(String(quantity ?? ''));
  return perUnit === undefined || !Number.isFinite(value) ? null : value * perUnit;
}

/**
 * The one figure an order's lines add up to, for a total or a price (card 989, MOR-D69): their sum when they share
 * a unit; in MT when they are all weights in different units (100 MT + 19,530 kg is 119.53 MT); null when a count
 * (rolls, reams) sits next to a weight or another count, which is never added to it: such lines are priced per line.
 * Lines with no unit at all add up with the unit ''.
 */
export function linesQuantityFigure(
  lines: readonly {
    quantity: number | string | null | undefined;
    unit: string | null | undefined;
  }[]
): { quantity: number; unit: string } | null {
  const unit = linesUnit(lines.map((line) => line.unit));
  // Lines with no unit at all are one (unknown) unit, added as before; one with no unit next to a known one is not
  const unitless = lines.length > 0 && lines.every((line) => !writtenUnit(line.unit));
  if (unit || unitless) {
    return {
      quantity: lines.reduce(
        (sum, line) => sum + (parseFloat(String(line.quantity ?? '')) || 0),
        0
      ),
      unit,
    };
  }
  const tons = lines.map((line) => quantityInTons(line.quantity, line.unit));
  if (!lines.length || tons.some((value) => value === null)) return null;
  return { quantity: tons.reduce((sum: number, value) => sum + value!, 0), unit: 'MT' };
}

/** An order line's quantity ("20.000000" is 20), or null when it has none: a missing or 0 quantity is never made 1 */
export function lineQuantity(quantity: string | number | null | undefined): number | null {
  const value = Number(quantity);
  return quantity !== null && quantity !== '' && value > 0 ? value : null;
}

/** One unit however it is written: "mt", "MT" and "metric tons" are the same, so are reels and rolls; "tons" and "MT" are not */
export function sameQuantityUnit(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  return canonicalUnit(a).toLowerCase() === canonicalUnit(b).toLowerCase();
}

/** A unit picker's choice for a unit however it is written ("mt" and "metric tons" are "MT"), or undefined */
export function listedUnit(
  choices: readonly string[],
  unit: string | null | undefined
): string | undefined {
  return writtenUnit(unit) ? choices.find((choice) => sameQuantityUnit(unit, choice)) : undefined;
}

/** A row's unit as its picker holds it: the listed choice, else the order line's own unit, else the fallback */
export function pickerUnit(
  choices: readonly string[],
  unit: string | null | undefined,
  fallback: string
): string {
  return listedUnit(choices, unit) ?? (writtenUnit(unit) || fallback);
}

/** A page's own units, then the order screen's units it lacks, so an order line's unit is always listed */
export function withOrderUnits(own: readonly string[]): readonly string[] {
  return [...own, ...QUANTITY_UNITS.filter((unit) => !listedUnit(own, unit))];
}

/** A unit picker's choices: the list, and the row's own unit when an order line brought one the list lacks */
export function unitChoices(
  choices: readonly string[],
  unit: string | null | undefined
): readonly string[] {
  const own = writtenUnit(unit);
  return own && !listedUnit(choices, own) ? [...choices, own] : choices;
}

/** Unit words of the unit pickers; codes (MT, KG, SQM) read the same in every language. */
const UNIT_OPTION_KEYS: Record<string, string> = {
  tons: 'common.tons',
  reams: 'common.reams',
  sheets: 'common.sheets',
  rolls: 'common.rolls',
  reels: 'common.reels',
  boxes: 'common.boxes',
  pallets: 'common.pallets',
};

/** A unit word after a number ("5 tons") in the user's language: "5 toneladas". Codes stay as they are. */
export function quantityUnitWord(
  unit: string | null | undefined,
  i18n: Pick<TranslationService, 'tOr'>
): string {
  const raw = writtenUnit(unit);
  const key = UNIT_OPTION_KEYS[raw.toLowerCase()];
  return key ? i18n.tOr(key, raw) : raw;
}

/** A unit picker option ("Tons", "Reams") in the user's language: "Toneladas", "Resmas". */
export function quantityUnitOptionLabel(
  unit: string,
  i18n: Pick<TranslationService, 'tOr'>
): string {
  const label = quantityUnitWord(unit, i18n);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** The units an order line's quantity can have (order screen) */
export const QUANTITY_UNITS = ['MT', 'Tons', 'KG', 'Reams', 'Sheets', 'Rolls', 'SQM'];

// Tonnage carries up to 3 decimals (9.999 MT, BUG-097)
const QUANTITY_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 });

/** A count or size as the screens write it: "2,500", "5.5" */
export function formatNumber(value: number): string {
  return QUANTITY_FORMAT.format(value);
}

const PLAIN_NUMBER = /^(-?)(\d+)(?:\.(\d+))?$/;

/**
 * A number saved as text ("20000.000000") as formatNumber writes it ("20,000"), without rounding: values that
 * differ in the 4th decimal still read differently, and a long number keeps all its digits. Other text as it is.
 */
export function formatExactNumber(text: string): string {
  const match = PLAIN_NUMBER.exec(text);
  if (!match) return text;
  const [, sign, whole, fraction = ''] = match;
  const decimals = fraction.replace(/0+$/, '');
  const grouped = whole.replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}${grouped}${decimals ? `.${decimals}` : ''}`;
}

/**
 * Total quantity of order lines, summed per unit and never across units:
 * "73.5 MT", or "20 MT + 500 reams" for mixed lines. Empty when nothing is known.
 * Unit words are in the reader's language ("500 resmas").
 */
export function formatQuantityTotals(
  lines: { quantity: number | string | null | undefined; unit: string | null | undefined }[],
  i18n: Pick<TranslationService, 'tOr'>
): string {
  // One total per unit (reels and rolls together), named as its first line writes it
  const totals = new Map<string, { unit: string; total: number }>();
  for (const line of lines) {
    const quantity = parseFloat(String(line.quantity ?? ''));
    if (!Number.isFinite(quantity)) continue;
    const key = canonicalUnit(line.unit);
    const sum = totals.get(key) ?? { unit: quantityUnitLabel(line.unit), total: 0 };
    totals.set(key, { ...sum, total: sum.total + quantity });
  }
  return [...totals.values()]
    .map(({ unit, total }) =>
      [QUANTITY_FORMAT.format(total), quantityUnitWord(unit, i18n)].filter(Boolean).join(' ')
    )
    .join(' + ');
}

/** One order line's quantity: "100 MT" for the API's "100.000000" + "mt". */
export function formatQuantity(
  quantity: number | string | null | undefined,
  unit: string | null | undefined,
  i18n: Pick<TranslationService, 'tOr'>
): string {
  return formatQuantityTotals([{ quantity, unit }], i18n);
}

/** formatQuantityTotals over an order's lines (items, else product_lines). */
export function orderQuantityLabel(
  order: { items?: any[] | null; product_lines?: any[] | null } | null | undefined,
  i18n: Pick<TranslationService, 'tOr'>
): string {
  return formatQuantityTotals(
    (order?.items || order?.product_lines || []).map((line) => ({
      quantity: line?.quantity_ordered ?? line?.quantity,
      unit: itemUnit(line),
    })),
    i18n
  );
}
